import VersionCompareKeyValueGrid from 'components/v2/compiler/version-compare/VersionCompareKeyValueGrid';
import { VersionCompareRow } from 'components/v2/compiler/version-compare/VersionCompareRow';
import { EpdVersionHistoryData } from 'components/v2/compiler/version-history/utils/EpdVersionHistoryDataContext';
import React from 'react';
import { useBiogas, useGetEpdElectricity } from 'services/api/queries';
import ComparePanelComponent from '../../../../pages/v2/compiler/ComparePanelComponent';
import { YES_NO } from '../constants';
import BiogasDataGridPdf from '../grids/biogas/BiogasDataGridPdf';
import ContributionOfScrapInputsGridPdf from '../grids/contribution-of-scrap-inputs/ContributionOfScrapInputsGridPdf';
import DeclarationOfCO2GridPdf from '../grids/declaration-of-co2/DeclarationOfCo2GridPdf';
import ElectricityGridPdf from '../grids/electricity/ElectricityGridPdf';
import { getElectricityHeaderText } from '../grids/electricity/ElectricityList';
import { getElectricityMethodRows } from '../grids/electricity/ElectricityListPdf';
import TransportationPerformanceGridPdf from '../grids/specification/TransportationPerformanceGridPdf';
import { getDataQualityAssessmentSummaryRow } from '../version-history/content/VersionHistoryDataQualityAssessment';
import VersionHistoryDataQualityAssessmentGrid from '../version-history/content/VersionHistoryDataQualityAssessmentGrid';
import { getDataQualityAssessmentCommentRow } from '../version-history/content/VersionHistoryDataQualityAssessmentGridComments';
import { getVersionLcaInformationRows } from '../version-history/content/VersionHistoryLcaInformationGrid';
import { mergeToLeftRightByIndex } from './utils/mergers';

const CompareLcaInformationSection: React.FC<{
  data: { left: EpdVersionHistoryData | null; right: EpdVersionHistoryData | null };
}> = ({ data }) => {
  if (!data.left || !data.right) return null;

  const biogasDataListLeft = useBiogas(data.left.epdVersionId).data;
  const biogasDataListRight = useBiogas(data.right.epdVersionId).data;
  const biogasDataListMerged = mergeToLeftRightByIndex(biogasDataListLeft, biogasDataListRight);

  const electricityItemsLeft = useGetEpdElectricity(data.left.epdVersionId).data;
  const electricityItemsRight = useGetEpdElectricity(data.right.epdVersionId).data;
  const electricityItemsMerged = mergeToLeftRightByIndex(electricityItemsLeft, electricityItemsRight);

  return (
    <ComparePanelComponent title="LCA information">
      <VersionCompareRow data={data}>
        {({ isLeft }) => <VersionCompareKeyValueGrid isLeft={isLeft} rowsGetter={getVersionLcaInformationRows} />}
      </VersionCompareRow>

      <VersionCompareRow data={data} compareByDom>
        {({ item, compareClassName }) => (
          <ContributionOfScrapInputsGridPdf className={compareClassName} epdVersionId={item!.epdVersionId} />
        )}
      </VersionCompareRow>

      <VersionCompareRow data={data} displayCondition={(x) => !!x?.dataQualityAssessmentAndReferenceYears}>
        {({ isLeft }) => (
          <VersionCompareKeyValueGrid
            isLeft={isLeft}
            rowsGetter={getDataQualityAssessmentSummaryRow}
            headerText="Data quality assessment and reference years"
          />
        )}
      </VersionCompareRow>
      <VersionCompareRow
        data={data}
        compareByDom
        displayCondition={(x) => !!x?.dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentItems?.length}
      >
        {({ item, compareClassName }) => (
          <VersionHistoryDataQualityAssessmentGrid
            className={compareClassName}
            dataQualityAssessmentAndReferenceYears={item!.dataQualityAssessmentAndReferenceYears}
          />
        )}
      </VersionCompareRow>
      <VersionCompareRow
        data={data}
        displayCondition={(x) =>
          !!x?.dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentSummary?.dataQualityAssessmentComment &&
          !!x.isPcrV2
        }
      >
        {({ item, isLeft }) => (
          <VersionCompareKeyValueGrid isLeft={isLeft} rowsGetter={getDataQualityAssessmentCommentRow} />
        )}
      </VersionCompareRow>

      {electricityItemsMerged.map((electricity) => (
        <React.Fragment key={electricity.left?.id || electricity.right?.id}>
          <VersionCompareRow data={electricity} displayCondition={(x) => !!x} compareByDom>
            {({ item, isLeft, compareClassName }) => {
              const isPcrV2 = isLeft ? data.left!.isPcrV2 ?? false : data.right!.isPcrV2 ?? false;
              const { electricityUsedInProcessName, isDefault } = item || {};
              return (
                <ElectricityGridPdf
                  className={compareClassName}
                  headerText={getElectricityHeaderText(isPcrV2, electricityUsedInProcessName, isDefault)}
                  data={item}
                  isPcrV2={isPcrV2}
                />
              );
            }}
          </VersionCompareRow>
          <VersionCompareRow data={electricity} displayCondition={(x) => !!x && !!x.residualElectricityMixCalculationMethod}>
            {({ isLeft }) => {
              return (
                <VersionCompareKeyValueGrid
                  isLeft={isLeft}
                  additionalData={electricity}
                  rowsGetter={getElectricityMethodRows}
                />
              );
            }}
          </VersionCompareRow>
        </React.Fragment>
      ))}

      {biogasDataListMerged.map((biogasData) => (
        <VersionCompareRow
          key={biogasData.left?.id || biogasData.right?.id}
          data={biogasData}
          displayCondition={(x) => !!x}
          compareByDom
        >
          {({ item, compareClassName }) => <BiogasDataGridPdf className={compareClassName} data={item} />}
        </VersionCompareRow>
      ))}

      <VersionCompareRow
        data={data}
        displayCondition={(x) => !!x?.lcaSpecification?.shouldTransportationPerformanceBeFetched}
        compareByDom
      >
        {({ item, compareClassName }) => (
          <TransportationPerformanceGridPdf className={compareClassName} epdVersionId={item!.epdVersionId} />
        )}
      </VersionCompareRow>

      <VersionCompareRow
        data={data}
        displayCondition={(x) => !!x?.lcaSpecification?.shouldDeclarationsBeFetched}
        compareByDom
      >
        {({ item, compareClassName }) => (
          <DeclarationOfCO2GridPdf
            className={compareClassName}
            epdVersionId={item!.epdVersionId}
            disabled={item?.cO2UptakeAssociation.association !== YES_NO.YES}
          />
        )}
      </VersionCompareRow>
    </ComparePanelComponent>
  );
};

export default CompareLcaInformationSection;
